import * as THREE from 'three';

export type BonusCardDimensions = Readonly<{
  width: number;
  height: number;
  bodyThickness: number;
  cornerRadius: number;
  bevelSize: number;
  bevelSegments: number;
  faceInset: number;
  trimWidth: number;
  accentRise: number;
}>;

export const BONUS_CARD_DIMENSIONS: BonusCardDimensions = Object.freeze({
  width: 2.72,
  height: 1.72,
  bodyThickness: 0.14,
  cornerRadius: 0.21,
  bevelSize: 0.035,
  bevelSegments: 4,
  faceInset: 0.065,
  trimWidth: 0.055,
  accentRise: 0.018,
});

export function createBonusCardBodyGeometry(
  dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS,
): THREE.ExtrudeGeometry {
  const bevelThickness = Math.min(dimensions.bevelSize, dimensions.bodyThickness * 0.22);
  const coreDepth = Math.max(0.01, dimensions.bodyThickness - bevelThickness * 2);
  const shape = createRoundedRectangleShape(
    dimensions.width,
    dimensions.height,
    dimensions.cornerRadius,
  );
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: coreDepth,
    steps: 1,
    curveSegments: 14,
    bevelEnabled: true,
    bevelSegments: dimensions.bevelSegments,
    bevelSize: dimensions.bevelSize,
    bevelThickness,
  });
  geometry.translate(0, 0, -coreDepth * 0.5);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createBonusCardFaceGeometry(
  dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS,
): THREE.ShapeGeometry {
  const geometry = new THREE.ShapeGeometry(
    createRoundedRectangleShape(
      dimensions.width - dimensions.faceInset * 2,
      dimensions.height - dimensions.faceInset * 2,
      Math.max(0.02, dimensions.cornerRadius - dimensions.faceInset),
    ),
    14,
  );
  normalizePlanarUvs(geometry);
  return geometry;
}

function normalizePlanarUvs(geometry: THREE.BufferGeometry): void {
  geometry.computeBoundingBox();
  const bounds = geometry.boundingBox;
  const position = geometry.getAttribute('position');
  if (!bounds || !position) return;
  const width = Math.max(Number.EPSILON, bounds.max.x - bounds.min.x);
  const height = Math.max(Number.EPSILON, bounds.max.y - bounds.min.y);
  const uv = new Float32Array(position.count * 2);
  for (let index = 0; index < position.count; index += 1) {
    uv[index * 2] = (position.getX(index) - bounds.min.x) / width;
    uv[index * 2 + 1] = (position.getY(index) - bounds.min.y) / height;
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

export function createBonusCardTrimGeometry(
  dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS,
): THREE.ExtrudeGeometry {
  const outerWidth = dimensions.width - dimensions.faceInset * 1.35;
  const outerHeight = dimensions.height - dimensions.faceInset * 1.35;
  const outerRadius = dimensions.cornerRadius - dimensions.faceInset * 0.45;
  const shape = createRoundedRectangleShape(outerWidth, outerHeight, outerRadius);
  const inner = new THREE.Path();
  appendRoundedRectangle(
    inner,
    outerWidth - dimensions.trimWidth * 2,
    outerHeight - dimensions.trimWidth * 2,
    Math.max(0.02, outerRadius - dimensions.trimWidth),
    true,
  );
  shape.holes.push(inner);

  const depth = dimensions.accentRise * 0.72;
  const bevelThickness = dimensions.accentRise * 0.14;
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth,
    steps: 1,
    curveSegments: 14,
    bevelEnabled: true,
    bevelSegments: 2,
    bevelSize: dimensions.accentRise * 0.11,
    bevelThickness,
  });
  geometry.translate(0, 0, -(depth * 0.5));
  geometry.computeVertexNormals();
  return geometry;
}

export function createBonusCardStudGeometry(
  dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS,
): THREE.SphereGeometry {
  return new THREE.SphereGeometry(dimensions.trimWidth * 0.48, 16, 8);
}

export function createBonusCardSparkGeometry(
  dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS,
): THREE.ExtrudeGeometry {
  const outerRadius = dimensions.height * 0.062;
  const innerRadius = outerRadius * 0.45;
  const shape = new THREE.Shape();
  const points = 16;
  for (let index = 0; index < points; index += 1) {
    const angle = Math.PI * 0.5 + (index / points) * Math.PI * 2;
    const radius = index % 2 === 0 ? outerRadius : innerRadius;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (index === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();

  const depth = dimensions.accentRise * 0.9;
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth,
    steps: 1,
    bevelEnabled: true,
    bevelSegments: 2,
    bevelSize: dimensions.accentRise * 0.14,
    bevelThickness: dimensions.accentRise * 0.16,
  });
  geometry.translate(0, 0, -depth * 0.5);
  geometry.computeVertexNormals();
  return geometry;
}

export function getBonusCardStudPositions(
  dimensions: BonusCardDimensions = BONUS_CARD_DIMENSIONS,
): readonly (readonly [number, number])[] {
  const x = dimensions.width * 0.5 - dimensions.faceInset * 1.75;
  const y = dimensions.height * 0.5 - dimensions.faceInset * 1.75;
  return [
    [-x, -y],
    [x, -y],
    [x, y],
    [-x, y],
  ] as const;
}

function createRoundedRectangleShape(width: number, height: number, radius: number): THREE.Shape {
  const shape = new THREE.Shape();
  appendRoundedRectangle(shape, width, height, radius, false);
  return shape;
}

function appendRoundedRectangle(
  path: THREE.Path,
  width: number,
  height: number,
  radius: number,
  clockwise: boolean,
): void {
  const halfWidth = width * 0.5;
  const halfHeight = height * 0.5;
  const safeRadius = Math.min(Math.max(radius, 0), halfWidth, halfHeight);

  path.moveTo(-halfWidth + safeRadius, -halfHeight);
  if (clockwise) {
    path.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth, -halfHeight + safeRadius);
    path.lineTo(-halfWidth, halfHeight - safeRadius);
    path.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth + safeRadius, halfHeight);
    path.lineTo(halfWidth - safeRadius, halfHeight);
    path.quadraticCurveTo(halfWidth, halfHeight, halfWidth, halfHeight - safeRadius);
    path.lineTo(halfWidth, -halfHeight + safeRadius);
    path.quadraticCurveTo(halfWidth, -halfHeight, halfWidth - safeRadius, -halfHeight);
  } else {
    path.lineTo(halfWidth - safeRadius, -halfHeight);
    path.quadraticCurveTo(halfWidth, -halfHeight, halfWidth, -halfHeight + safeRadius);
    path.lineTo(halfWidth, halfHeight - safeRadius);
    path.quadraticCurveTo(halfWidth, halfHeight, halfWidth - safeRadius, halfHeight);
    path.lineTo(-halfWidth + safeRadius, halfHeight);
    path.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth, halfHeight - safeRadius);
    path.lineTo(-halfWidth, -halfHeight + safeRadius);
    path.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth + safeRadius, -halfHeight);
  }
  path.closePath();
}
